import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useAppState } from "../../../context/StateContext";
import useActionPorts from "../../../hooks/useActionPorts";
import useActionPurchaseOrder from "../../../hooks/useActionPurchaseOrder";
import { usePurchaseOrderContext } from "../../../hooks/usePurchaseOrderContext";
import { Carton } from "../../../models/carton";
import CurrencyService from "../../../services/currency.service";
import { CompanyProfile, CountryCode, Status } from "../../../util/enums";
import { CurrenciesKeys } from "../../../util/queries";
import { convertCartonProductsToCartonsView } from "../Maintenance/types";

interface UsePurchaseOrdersProps {
  viewOnly?: boolean;
  purchaseOrderId?: string;
}

export const usePurchaseOrders = ({ viewOnly, purchaseOrderId }: UsePurchaseOrdersProps) => {
  const { currentUser } = useAppState();

  const { getPurchaseOrder, getCartonsFromOrder } = useActionPurchaseOrder();

  const { getAllPorts } = useActionPorts();

  const { purchaseOrder, setPurchaseOrder, handleUpdatePurchaseOrder, cartons, setCartons } =
    usePurchaseOrderContext();
  const { data: purchaseOrderData, isFetching: fetchingPurchaseOrder } = getPurchaseOrder(
    Number(purchaseOrderId)
  );

  useEffect(() => {
    if (purchaseOrderData) {
      setPurchaseOrder(purchaseOrderData);
    }
  }, [purchaseOrderData]);

  const { isFetching: loadingOrderCartons } = getCartonsFromOrder(
    Number(purchaseOrder?.id),
    (cartons: Carton[]) => setCartons(convertCartonProductsToCartonsView(purchaseOrder, cartons))
  );

  const companyId =
    currentUser?.company?.profile?.initials === CompanyProfile.SPP
      ? currentUser.company.supplierAssociated?.companyId
      : currentUser?.companyId;

  const currenciesResponse = useQuery({
    queryKey: CurrenciesKeys.getAll([currentUser?.companyId, Status.ACTIVE]),
    queryFn: async () => {
      const response = await CurrencyService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      });

      if (response && response.data) return response.data;

      return [];
    },
  });
  const { data: portCodes, isFetching: isLoadingPorts } = getAllPorts({ companyId });

  const isDestinationPortFromBrazil = useMemo(() => {
    const countryCode = purchaseOrder?.destinationPort?.country?.code;

    return countryCode === CountryCode.BRAZIL;
  }, [purchaseOrder?.destinationPort, portCodes, viewOnly]);

  return {
    currenciesResponse,
    isDestinationPortFromBrazil,
    loadingOrderCartons,
    isLoadingPorts,
    portCodes,
    handleUpdatePurchaseOrder,
    fetchingPurchaseOrder,
    cartons,
    purchaseOrder,
    setCartons,
  };
};
